import { useCallback, useEffect, useMemo, useState } from "react";
import { _get, _post, _put } from "../../../libraries/fetch";
import {
  getOnboardingStatusByBranchURL,
  saveOnboardingStatusURL,
  updateTecgymAdminOnboardingStatusURL
} from "../../../libraries/apiUrl";
import { showToast } from "../../../libraries/clipboardUtils";
import { buildActivityChangeValues, withAppActivityFields } from "../../../libraries/activityLogger";

const STATUS_OPTIONS = ["Pending", "Complete", "Not Required"];
const CONSOLE_APP_OPTIONS = ["ZKTimes", "WDMS"];
const CONSOLE_APP_STEP_KEY = "consoleApp";

const STATUS_STYLES = {
  Pending: {
    backgroundColor: "#7f1d1d",
    borderColor: "#b91c1c"
  },
  Complete: {
    backgroundColor: "#14532d",
    borderColor: "#16a34a"
  },
  ZKTimes: {
    backgroundColor: "#14532d",
    borderColor: "#16a34a"
  },
  WDMS: {
    backgroundColor: "#14532d",
    borderColor: "#16a34a"
  },
  "Not Required": {
    backgroundColor: "#374151",
    borderColor: "#6b7280"
  }
};

const ONBOARDING_STEPS = [
  { label: "Onboarding Initiated", key: "onboardingIntiated" },
  { label: "Create Users", key: "createUsers" },
  { label: "Create Packages", key: "createPackages" },
  { label: "Add payment details", key: "addPaymentDetails" },
  { label: "Set privileges", key: "setPrivileges" },
  { label: "Data Import", key: "dataImport" },
  { label: "Standeez/ QA", key: "standeezQA" },
  { label: "Whole App QA", key: "wholeAppQA" },
  { label: "Console app", key: "consoleApp" },
  { label: "Istallation", key: "installation" },
  { label: "Training", key: "training" },
  { label: "Console Task scheduler", key: "consoleTaskScheduler" }
];

const headerRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: "16px",
  marginBottom: "20px",
  flexWrap: "wrap"
};

const actionButtonStyle = {
  border: "1px solid #187718",
  background: "linear-gradient(135deg, #187718 0%, #20a020 100%)",
  color: "#fff",
  borderRadius: "10px",
  padding: "12px 18px",
  fontSize: "14px",
  fontWeight: 800,
  cursor: "pointer",
  whiteSpace: "nowrap",
  boxShadow: "0 4px 12px rgba(24, 119, 24, 0.3)"
};

const confirmOverlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 1300,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "18px",
  backgroundColor: "rgba(15, 23, 42, 0.68)",
  backdropFilter: "blur(4px)"
};

const confirmCardStyle = {
  width: "min(360px, 100%)",
  padding: "26px",
  borderRadius: "18px",
  backgroundColor: "#fff",
  color: "#111827",
  textAlign: "center",
  boxShadow: "0 24px 70px rgba(0, 0, 0, 0.42)"
};

const confirmTitleStyle = {
  margin: "0 0 10px",
  fontSize: "22px",
  fontWeight: 800
};

const confirmMessageStyle = {
  margin: "0 0 20px",
  fontSize: "14px",
  lineHeight: 1.5,
  color: "#4b5563"
};

const confirmActionsStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "10px"
};

const confirmNoButtonStyle = {
  height: "42px",
  border: "1px solid #d1d5db",
  borderRadius: "12px",
  backgroundColor: "#fff",
  color: "#111827",
  fontSize: "14px",
  fontWeight: 700,
  cursor: "pointer"
};

function confirmYesButtonStyle() {
  return {
    height: "42px",
    border: "none",
    borderRadius: "12px",
    backgroundColor: "#187718",
    color: "#fff",
    fontSize: "14px",
    fontWeight: 800,
    cursor: "pointer"
  };
}

function confirmIconStyle() {
  return {
    width: "52px",
    height: "52px",
    margin: "0 auto 14px",
    borderRadius: "50%",
    backgroundColor: "#dcfce7",
    color: "#187718",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
    fontWeight: 800
  };
}

const getNormalizedId = (value) => {
  if (value == null || value === "") {
    return "";
  }

  if (typeof value === "object") {
    if (value.$oid) {
      return String(value.$oid).trim();
    }

    return String(
      value._id ||
      value.id ||
      value.branchId ||
      value.companyId ||
      ""
    ).trim();
  }

  return String(value).trim();
};

const resolveBranchId = (branchId, branch) => {
  return getNormalizedId(
    branchId ||
    branch?._id ||
    branch?.id ||
    branch?.branchId ||
    branch?.branch_id ||
    branch?.branchID
  );
};

const resolveCompanyId = (companyId, branch) => {
  return getNormalizedId(
    companyId ||
    branch?.company ||
    branch?.companyId ||
    branch?.company_id ||
    branch?.companyID
  );
};

const getOnboardingStatusListFromResponse = (response) => {
  if (!response) {
    return [];
  }

  const candidates = [
    response,
    response?.onboardingStatuses,
    response?.onboardingStatus,
    response?.data?.onboardingStatuses,
    response?.data?.onboardingStatus,
    response?.data?.items,
    response?.data,
    response?.response?.onboardingStatuses,
    response?.response?.onboardingStatus,
    response?.response,
    response?.records,
    response?.items
  ];

  const list = candidates.find(Array.isArray);
  if (list) {
    return list;
  }

  const singleRecord = getOnboardingStatusRecordFromResponse(response);
  return singleRecord ? [singleRecord] : [];
};

const getOnboardingStatusRecordFromResponse = (response) => {
  if (!response || typeof response !== "object" || Array.isArray(response)) {
    return null;
  }

  if (response._id || response.id || response.branchId || response.companyId) {
    return response;
  }

  const nested =
    response?.onboardingStatus ||
    response?.data?.onboardingStatus ||
    response?.response?.onboardingStatus ||
    response?.data ||
    response?.response ||
    response?.result;

  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return nested;
  }

  return null;
};

const toCamelCase = (value) => {
  return String(value || "")
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, char) => char.toUpperCase())
    .replace(/^[A-Z]/, (char) => char.toLowerCase());
};

const toSnakeCase = (value) => {
  return String(value || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[\s/-]+/g, "_")
    .toLowerCase();
};

const getStepKeyVariants = (step) => {
  return [...new Set([
    step.key,
    step.label,
    toCamelCase(step.label),
    toSnakeCase(step.label),
    toSnakeCase(step.key),
    step.label.toLowerCase(),
    step.key.toLowerCase()
  ].filter(Boolean))];
};

const normalizeConsoleAppValue = (value) => {
  if (value == null || value === "") {
    return null;
  }

  if (CONSOLE_APP_OPTIONS.includes(value)) {
    return value;
  }

  const normalized = String(value).trim().toLowerCase().replace(/[\s_-]+/g, "");

  if (normalized === "zktimes" || normalized === "zk") {
    return "ZKTimes";
  }

  if (normalized === "wdms") {
    return "WDMS";
  }

  return null;
};

const normalizeStatusValue = (value, stepKey = null) => {
  if (value == null || value === "") {
    return null;
  }

  if (stepKey === CONSOLE_APP_STEP_KEY) {
    const consoleAppValue = normalizeConsoleAppValue(value);
    if (consoleAppValue) {
      return consoleAppValue;
    }
  }

  if (STATUS_OPTIONS.includes(value)) {
    return value;
  }

  if (CONSOLE_APP_OPTIONS.includes(value)) {
    return value;
  }

  const normalized = String(value).trim().toLowerCase().replace(/[\s_-]+/g, "");

  if (normalized === "pending") {
    return "Pending";
  }

  if (normalized === "complete" || normalized === "completed" || normalized === "done") {
    return "Complete";
  }

  if (normalized === "notrequired" || normalized === "notreq") {
    return "Not Required";
  }

  const consoleAppValue = normalizeConsoleAppValue(value);
  if (consoleAppValue) {
    return consoleAppValue;
  }

  return null;
};

const flattenOnboardingRecord = (record) => {
  if (!record || typeof record !== "object") {
    return {};
  }

  return {
    ...record,
    ...(record.onboardingStatus && typeof record.onboardingStatus === "object" ? record.onboardingStatus : {}),
    ...(record.statuses && typeof record.statuses === "object" ? record.statuses : {}),
    ...(record.steps && typeof record.steps === "object" ? record.steps : {}),
    ...(record.properties && typeof record.properties === "object" ? record.properties : {})
  };
};

const getStepValueFromRecord = (record, step) => {
  const flatRecord = flattenOnboardingRecord(record);
  const valuesByLowerKey = Object.entries(flatRecord).reduce((acc, [key, value]) => {
    acc[key.toLowerCase()] = value;
    return acc;
  }, {});

  for (const variant of getStepKeyVariants(step)) {
    const value =
      flatRecord[variant] ??
      valuesByLowerKey[String(variant).toLowerCase()];
    const normalized = normalizeStatusValue(value, step.key);

    if (normalized) {
      return normalized;
    }
  }

  return null;
};

const getSelectOptionsForStep = (stepKey, currentStatus) => {
  if (stepKey !== CONSOLE_APP_STEP_KEY) {
    return STATUS_OPTIONS;
  }

  if (CONSOLE_APP_OPTIONS.includes(currentStatus)) {
    return [...STATUS_OPTIONS, ...CONSOLE_APP_OPTIONS];
  }

  return STATUS_OPTIONS;
};

const getOnboardingStatusRecordId = (record) => {
  return getNormalizedId(record?._id || record?.id || record?.onboardingStatusId || "");
};

const getDefaultStatusByStep = (value = "Pending") => {
  return ONBOARDING_STEPS.reduce((acc, step) => {
    acc[step.key] = value;
    return acc;
  }, {});
};

const hydrateStatusByStep = (record) => {
  const nextStatus = getDefaultStatusByStep();

  ONBOARDING_STEPS.forEach((step) => {
    const value = getStepValueFromRecord(record, step);

    if (value) {
      nextStatus[step.key] = value;
    }
  });

  return nextStatus;
};

const findOnboardingStatusForBranch = (records, normalizedCompanyId, normalizedBranchId) => {
  if (!Array.isArray(records) || records.length === 0) {
    return null;
  }

  const matchByBranchAndCompany = records.find((record) => {
    const recordBranchId = getNormalizedId(
      record?.branchId || record?.branch || record?.branch_id || record?.branchID
    );
    const recordCompanyId = getNormalizedId(
      record?.companyId || record?.company || record?.company_id || record?.companyID
    );

    return recordBranchId === normalizedBranchId && recordCompanyId === normalizedCompanyId;
  });

  if (matchByBranchAndCompany) {
    return matchByBranchAndCompany;
  }

  return records.find((record) => {
    const recordBranchId = getNormalizedId(
      record?.branchId || record?.branch || record?.branch_id || record?.branchID
    );

    return recordBranchId === normalizedBranchId;
  }) || null;
};

const resolveOnboardingStatusRecord = (response, normalizedCompanyId, normalizedBranchId) => {
  const singleRecord = getOnboardingStatusRecordFromResponse(response);

  if (
    singleRecord &&
    !Array.isArray(response) &&
    !Array.isArray(response?.data) &&
    !Array.isArray(response?.response)
  ) {
    const recordBranchId = getNormalizedId(
      singleRecord?.branchId || singleRecord?.branch || singleRecord?.branch_id || singleRecord?.branchID
    );
    const recordCompanyId = getNormalizedId(
      singleRecord?.companyId || singleRecord?.company || singleRecord?.company_id || singleRecord?.companyID
    );

    if (recordBranchId === normalizedBranchId && recordCompanyId === normalizedCompanyId) {
      return singleRecord;
    }
  }

  const allStatuses = getOnboardingStatusListFromResponse(response);
  return findOnboardingStatusForBranch(allStatuses, normalizedCompanyId, normalizedBranchId);
};

const buildOnboardingStatusPayload = (companyId, branchId, statusByStep, activityOptions = null) => {
  const payload = {
    ...statusByStep,
    companyId,
    branchId
  };

  if (!activityOptions) {
    return payload;
  }

  return withAppActivityFields(payload, activityOptions);
};

const getApiResponseMessage = (response) => {
  if (typeof response === "string") {
    return response.trim();
  }

  const message =
    response?.message ||
    response?.data?.message ||
    response?.response?.message ||
    response?.error ||
    response?.data?.error ||
    response?.response?.error;

  return typeof message === "string" ? message.trim() : "";
};

const showApiResponseMessage = (response, successType = "success", fallbackSuccess, fallbackError) => {
  const message = getApiResponseMessage(response);

  if (response?.success === false || response === null) {
    showToast(message || fallbackError || "Request failed. Please try again.", "error");
    return false;
  }

  showToast(message || fallbackSuccess || "Request completed successfully.", successType);
  return true;
};

function OnboardingStatus({ branchId, branchName, companyId, branch }) {
  const [statusByStep, setStatusByStep] = useState(() => getDefaultStatusByStep());
  const [savedStatusByStep, setSavedStatusByStep] = useState(() => getDefaultStatusByStep());
  const [statusRecordId, setStatusRecordId] = useState("");
  const [hasSavedStatus, setHasSavedStatus] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [consoleAppDialogOpen, setConsoleAppDialogOpen] = useState(false);
  const [loadError, setLoadError] = useState("");

  const normalizedBranchId = useMemo(() => resolveBranchId(branchId, branch), [branchId, branch]);
  const normalizedCompanyId = useMemo(() => resolveCompanyId(companyId, branch), [companyId, branch]);

  const applyOnboardingStatusRecord = useCallback((record) => {
    if (!record) {
      setStatusRecordId("");
      setHasSavedStatus(false);
      setStatusByStep(getDefaultStatusByStep());
      setSavedStatusByStep(getDefaultStatusByStep());
      return;
    }

    const recordId = getOnboardingStatusRecordId(record);
    const hydratedStatus = hydrateStatusByStep(record);

    setStatusRecordId(recordId);
    setHasSavedStatus(true);
    setStatusByStep(hydratedStatus);
    setSavedStatusByStep(hydratedStatus);
  }, []);

  const loadOnboardingStatuses = useCallback(async () => {
    if (!normalizedBranchId) {
      applyOnboardingStatusRecord(null);
      return;
    }

    setIsLoading(true);
    setLoadError("");

    try {
      const response = await _get(getOnboardingStatusByBranchURL(normalizedBranchId), { useCache: false });

      if (response === null) {
        throw new Error("Failed to load onboarding status.");
      }

      if (response?.success === false || response?.status === false) {
        throw new Error(getApiResponseMessage(response) || "Failed to load onboarding status.");
      }

      const branchStatus = resolveOnboardingStatusRecord(
        response,
        normalizedCompanyId,
        normalizedBranchId
      );
      applyOnboardingStatusRecord(branchStatus || null);
    } catch (error) {
      const errorMessage = error.message || "Failed to load onboarding status.";
      setLoadError(errorMessage);
      applyOnboardingStatusRecord(null);
      showToast(errorMessage, "error");
    } finally {
      setIsLoading(false);
    }
  }, [applyOnboardingStatusRecord, normalizedBranchId, normalizedCompanyId]);

  useEffect(() => {
    loadOnboardingStatuses();
  }, [loadOnboardingStatuses]);

  const handleStepChange = (stepKey, nextValue) => {
    if (stepKey === CONSOLE_APP_STEP_KEY && nextValue === "Complete") {
      setConsoleAppDialogOpen(true);
      return;
    }

    setStatusByStep((currentStatus) => ({
      ...currentStatus,
      [stepKey]: nextValue
    }));
  };

  const handleCloseConsoleAppDialog = () => {
    setConsoleAppDialogOpen(false);
  };

  const handleSelectConsoleAppOption = (option) => {
    setStatusByStep((currentStatus) => ({
      ...currentStatus,
      [CONSOLE_APP_STEP_KEY]: option
    }));
    setConsoleAppDialogOpen(false);
  };

  const handleOpenConfirmDialog = () => {
    if (!normalizedBranchId) {
      showToast("Branch ID is required before saving onboarding status.", "error");
      return;
    }

    if (!normalizedCompanyId) {
      showToast("Company ID is required before saving onboarding status.", "error");
      return;
    }

    if (hasSavedStatus) {
      setConfirmDialog({
        type: "update",
        title: "Confirm Update",
        message: "Are you sure you want to update onboarding status for this client?",
        confirmLabel: "Yes",
        cancelLabel: "No"
      });
      return;
    }

    setConfirmDialog({
      type: "add",
      title: "Confirm Add",
      message: "Are you sure you want to add onboarding status with all steps set to Pending?",
      confirmLabel: "Yes",
      cancelLabel: "No"
    });
  };

  const handleCloseConfirmDialog = () => {
    if (!isSubmitting) {
      setConfirmDialog(null);
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmDialog || !normalizedBranchId || !normalizedCompanyId) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (confirmDialog.type === "add") {
        const pendingStatus = getDefaultStatusByStep("Pending");
        const payload = buildOnboardingStatusPayload(
          normalizedCompanyId,
          normalizedBranchId,
          pendingStatus,
          {
            activityType: "Add Onboarding Status",
            initiatedFrom: "Onboarding Status",
            name: branchName || normalizedBranchId,
            previousValue: null,
            updatedValue: "Onboarding Object added"
          }
        );
        const response = await _post(saveOnboardingStatusURL(normalizedBranchId), payload);

        const isSuccess = showApiResponseMessage(
          response,
          "success",
          "Onboarding status added successfully.",
          "Failed to add onboarding status."
        );

        if (!isSuccess) {
          return;
        }
      } else {
        if (!statusRecordId) {
          showToast("Onboarding status record not found. Please refresh and try again.", "error");
          return;
        }

        const stepKeys = ONBOARDING_STEPS.map((step) => step.key);
        const { previousValue, updatedValue } = buildActivityChangeValues(
          savedStatusByStep,
          statusByStep,
          stepKeys
        );
        const payload = buildOnboardingStatusPayload(
          normalizedCompanyId,
          normalizedBranchId,
          statusByStep,
          {
            activityType: "Update Onboarding Status",
            initiatedFrom: "Onboarding Status",
            name: branchName || normalizedBranchId,
            previousValue,
            updatedValue
          }
        );
        const response = await _put(updateTecgymAdminOnboardingStatusURL(normalizedBranchId), payload);

        const isSuccess = showApiResponseMessage(
          response,
          "success",
          "Onboarding status updated successfully.",
          "Failed to update onboarding status."
        );

        if (!isSuccess) {
          return;
        }
      }

      setConfirmDialog(null);
      await loadOnboardingStatuses();
    } catch (error) {
      showToast(error.message || "Failed to save onboarding status.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="configMain">
      <div style={headerRowStyle}>
        <h1 style={{ margin: 0 }}>Onboarding Status</h1>
        <button
          type="button"
          style={{
            ...actionButtonStyle,
            opacity: isLoading || isSubmitting ? 0.7 : 1,
            cursor: isLoading || isSubmitting ? "not-allowed" : "pointer"
          }}
          onClick={handleOpenConfirmDialog}
          disabled={isLoading || isSubmitting || !normalizedBranchId || !normalizedCompanyId}
        >
          {hasSavedStatus ? "Update Status" : "Add Status"}
        </button>
      </div>

      {isLoading && (
        <div style={{ marginBottom: "10px", color: "#9ca3af", fontSize: "13px" }}>
          Loading onboarding status...
        </div>
      )}

      {!isLoading && loadError && (
        <div style={{ marginBottom: "10px", color: "#f87171", fontSize: "13px" }}>
          {loadError}
        </div>
      )}

      {isSubmitting && (
        <div style={{ marginBottom: "10px", color: "#9ca3af", fontSize: "13px" }}>
          Saving onboarding status...
        </div>
      )}

      {hasSavedStatus && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
            gap: "12px"
          }}
        >
          {ONBOARDING_STEPS.map((step) => {
            const currentStatus = statusByStep[step.key] || "Pending";
            const currentStyle = STATUS_STYLES[currentStatus] || STATUS_STYLES.Pending;
            const selectOptions = getSelectOptionsForStep(step.key, currentStatus);

            return (
              <div
                key={step.key}
                style={{
                  backgroundColor: "#222",
                  border: "1px solid #3f3f46",
                  borderRadius: "8px",
                  padding: "12px"
                }}
              >
                <div
                  style={{
                    color: "#fff",
                    fontWeight: 600,
                    marginBottom: "10px",
                    minHeight: "40px",
                    lineHeight: 1.3
                  }}
                >
                  {step.label}
                </div>
                <select
                  value={currentStatus}
                  onChange={(event) => handleStepChange(step.key, event.target.value)}
                  disabled={isLoading || isSubmitting}
                  style={{
                    padding: "8px 10px",
                    borderRadius: "6px",
                    border: `1px solid ${currentStyle.borderColor}`,
                    backgroundColor: currentStyle.backgroundColor,
                    color: "#fff",
                    width: "100%"
                  }}
                >
                  {selectOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      )}

      {consoleAppDialogOpen && (
        <div
          style={confirmOverlayStyle}
          role="dialog"
          aria-modal="true"
          aria-label="Select Console App"
        >
          <div style={confirmCardStyle}>
            <div style={confirmIconStyle()}>?</div>
            <h3 style={confirmTitleStyle}>Select Console App</h3>
            <p style={confirmMessageStyle}>
              Choose which console app was completed for this client.
            </p>
            <div style={confirmActionsStyle}>
              {CONSOLE_APP_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => handleSelectConsoleAppOption(option)}
                  disabled={isSubmitting}
                  style={confirmYesButtonStyle()}
                >
                  {`( ${option} )`}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={handleCloseConsoleAppDialog}
              disabled={isSubmitting}
              style={{
                ...confirmNoButtonStyle,
                width: "100%",
                marginTop: "10px"
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {confirmDialog && (
        <div style={confirmOverlayStyle} role="dialog" aria-modal="true" aria-label={confirmDialog.title}>
          <div style={confirmCardStyle}>
            <div style={confirmIconStyle()}>?</div>
            <h3 style={confirmTitleStyle}>{confirmDialog.title}</h3>
            <p style={confirmMessageStyle}>{confirmDialog.message}</p>
            <div style={confirmActionsStyle}>
              <button
                type="button"
                onClick={handleCloseConfirmDialog}
                disabled={isSubmitting}
                style={confirmNoButtonStyle}
              >
                {confirmDialog.cancelLabel}
              </button>
              <button
                type="button"
                onClick={handleConfirmAction}
                disabled={isSubmitting}
                style={confirmYesButtonStyle()}
              >
                {isSubmitting ? "Please wait..." : confirmDialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OnboardingStatus;
